import { functions } from '../firebase'; 
import { httpsCallable } from 'firebase/functions';

const call = async (funcName, data = {}) => {
    try {
        const firebaseFunc = httpsCallable(functions, funcName);
        const result = await firebaseFunc(data);
        return { data: result.data }; 
    } catch (error) {
        console.error(`Firebase Function Error (${funcName}):`, error);
        const err = new Error(error.message || 'An unknown error occurred');
        err.response = { data: { message: err.message } };
        throw err;
    }
};

// --- Server Management ---
export const fetchServersAPI = () => call('fetchServers');
export const fetchArchivedServersAPI = () => call('fetchServers', { status: 'archived' });
export const createServerAPI = (serverData) => call('createServer', serverData);
export const getServerDetailsAPI = (serverId) => call('fetchSingleServer', { serverId });
export const updateServerAPI = (serverId, updatedData) => call('updateServer', { serverId, ...updatedData });
export const hardDeleteServerAPI = (serverId) => call('hardDeleteServer', { serverId });

// --- Global User Management ---
export const createUserAPI = (userData) => {
    if (userData.role === 'admin') return call('createAdmins', userData);
    if (userData.role === 'employee') return call('createEmployees', userData);
    if (userData.role === 'serviceUser') return call('createServiceUsers', userData);
};

export const updateUserAPI = (userId, updatedData) => {
    return call('updateUser', { userId, ...updatedData });
};

export const toggleUserStatusAPI = (payload) => {
    let data = typeof payload === 'string' ? { uid: payload, userId: payload } : { ...payload };
    if (data.userId && !data.uid) data.uid = data.userId;
    if (data.uid && !data.userId) data.userId = data.uid;
    return call('toggleUserStatus', data);
};

export const deleteUserAPI = (payload) => {
    let data = typeof payload === 'string' ? { uid: payload, userId: payload } : { ...payload };
    if (data.userId && !data.uid) data.uid = data.userId;
    if (data.uid && !data.userId) data.userId = data.uid;
    return call('deleteUser', data); 
};

export const getAllUsersAPI = () => call('getAllUsers');
export const addUserToServerAPI = (serverId, userId) => call('addUserToServer', { serverId, userId });
export const searchAdminsForLinkingAPI = (query, targetServerId) => call('searchAdminsForLinking', { query, targetServerId });
export const linkAdminToServerAPI = (payload) => call('linkAdminToServer', payload);
export const lookupEmailByUsernameAPI = (serverId, username) => call('lookupEmailByUsername', { serverId, username });

export const removeUserFromServerAPI = (arg1, arg2) => {
    if (typeof arg1 === 'object' && arg1 !== null) {
        let data = { ...arg1 };
        if (data.userId && !data.uid) data.uid = data.userId;
        return call('removeUserFromServer', data);
    }
    return call('removeUserFromServer', { serverId: arg1, userId: arg2, uid: arg2 });
};

// --- Server-Specific "Read" API Calls ---
export const getAdminsForServerAPI = (serverId) => call('getAllUsersByRoleForServer', { serverId, role: 'admin' });
export const getEmployeesForServerAPI = (serverId) => call('getAllUsersByRoleForServer', { serverId, role: 'employee' });
export const getServerStatusHistoryAPI = (serverId) => call('fetchServerStatusHistory', { serverId });
export const createGlobalAdminAPI = (userData) => call('createGlobalAdmin', userData);
export const getServerUsersLoginHistoryAPI = (serverId) => call('fetchServerUsersLoginHistory', { serverId });
export const getUserDeviceAccessAPI = (serverId, role, userId) => call('fetchUserDeviceAccess', { serverId, role, userId });
export const sendPasswordResetAPI = (payload) => call('sendPasswordReset', payload);
export const getServiceUsersForServerAPI = async (serverId, page = 1, limit = 50) => {
    try {
        const getServiceUsers = httpsCallable(functions, 'getAllUsersByRoleForServer');
        const response = await getServiceUsers({ serverId, role: 'serviceUser', page, limit });
        return response;
    } catch (error) {
        throw error;
    }
};

export const bulkCloneServiceUsersAPI = async (payload) => {
    try {
        const bulkCloneServiceUsers = httpsCallable(functions, 'bulkCloneServiceUsers');
        const response = await bulkCloneServiceUsers(payload);
        return response;
    } catch (error) {
        throw error;
    }
};

export const bulkCloneStaffAPI = async (payload) => {
    try {
        const bulkCloneStaff = httpsCallable(functions, 'bulkCloneStaff');
        const response = await bulkCloneStaff(payload);
        return response;
    } catch (error) {
        throw error;
    }
};

export const bulkCreateUsersAPI = async (payload) => {
    try {
        const bulkCreateUsers = httpsCallable(functions, 'bulkCreateUsers');
        const response = await bulkCreateUsers(payload);
        return response;
    } catch (error) {
        throw error;
    }
};

export const sendServerBroadcastAPI = (payload) => call('sendServerBroadcast', payload);
export const fetchServerBroadcastHistoryAPI = (serverId) => call('fetchServerBroadcastHistory', { serverId });
export const sendGlobalBroadcastAPI = (payload) => call('sendGlobalBroadcast', payload);
export const fetchLatestBroadcastsAPI = () => call('getDistinctGlobalBroadcasts');

// --- SUPPORT TICKETING API ---
export const createTicketAPI = (data) => call('createTicket', data);
export const fetchAllTicketsAPI = () => call('fetchAllTickets');
export const updateTicketStatusAPI = (ticketId, newStatus) => call('updateTicketStatus', { ticketId, newStatus });
export const assignTicketAPI = (ticketId, assigneeUid, assigneeName) => call('assignTicket', { ticketId, assigneeUid, assigneeName });
export const addTicketCommentAPI = (ticketId, message, isInternalOnly, attachments = []) => call('addTicketComment', { ticketId, message, isInternalOnly, attachments });
export const fetchSupportStaffAPI = () => call('fetchSupportStaff');
export const fetchTicketCommentsAPI = (ticketId) => call('fetchTicketComments', { ticketId });
export const requestSupportOtpAPI = (email, username = null, isNewTicket = false) => call('requestSupportOTP', { email, username, isNewTicket });
export const verifyOtpAndSubmitTicketAPI = (email, otp, ticketData) => call('verifyOtpAndSubmitTicket', { email, otp, ticketData });
export const updateTicketPriorityAPI = (ticketId, newPriority) => call('updateTicketPriority', { ticketId, newPriority });
export const fetchRoutingRulesAPI = () => call('fetchRoutingRules');
export const updateRoutingRuleAPI = (data) => call('updateRoutingRule', data);
export const generateSecureUploadUrlAPI = (fileData) => call('generateSecureUploadUrl', fileData);
export const verifyOtpAndFetchTicketAPI = (email, otp, ticketId) => call('verifyOtpAndFetchTicket', { email, otp, ticketId });
export const addPublicTicketCommentAPI = (ticketId, email, message, attachments) => call('addPublicTicketComment', { ticketId, email, message, attachments });
export const updateTicketCollaboratorsAPI = (ticketId, collaborators) => call('updateTicketCollaborators', { ticketId, collaborators });
export const fetchTicketLogsAPI = (ticketId) => call('fetchTicketLogs', { ticketId });
export const submitTicketFeedbackAPI = (feedbackData) => call('submitTicketFeedback', feedbackData);
export const generateSecureReadUrlAPI = (fileUrl) => call('generateSecureReadUrl', { fileUrl });
export const updateTicketServerIdAPI = (ticketId, newServerId) => call('updateTicketServerId', { ticketId, newServerId });
export const fetchTicketsForExportAPI = (filters) => call('fetchTicketsForExport', filters);
export const refreshPublicTicketAPI = (data) => call('refreshPublicTicket', data);
export const validateFeedbackTicketAPI = (data) => call('validateFeedbackTicket', data);
export const updateTicketFollowUpAPI = (data) => call('updateTicketFollowUp', data);
export const assignFollowUpAPI = (data) => call('assignFollowUp', data);
export const fetchSupportAnalyticsAPI = () => call('fetchSupportAnalytics');
export const fetchAuditLogsAPI = (filters) => call('getAuditLogs', filters);
export const rollbackAuditLogAPI = (logId) => call('rollbackAuditLog', { logId });